import { dictionaryApi } from './dictionaryClient';
import {
  associatedBarnasToBody,
  normalizeAssociatedBarna,
  type AssociatedBarna,
} from './pratyaharApi';

export type { AssociatedBarna };
export { associatedBarnasToBody };

export type BarnaSamuha = {
  id: number;
  name: string;
  synonyms: string[];
  associatedBarnas: AssociatedBarna[];
};

export type CreateBarnaSamuhaBody = {
  name: string;
  synonyms: string[];
  associated_barnas: Array<{ barna_id: number; included_bhedas: number[] }>;
};

export type UpdateBarnaSamuhaBody = Partial<CreateBarnaSamuhaBody>;

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function toNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function toStr(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

export function normalizeBarnaSamuha(raw: unknown): BarnaSamuha | null {
  if (!isRecord(raw)) return null;
  const id = toNum(raw.id);
  if (id == null) return null;
  const synRaw = raw.synonyms;
  const synonyms = Array.isArray(synRaw) ? synRaw.map((x) => toStr(x)) : [];
  const assocRaw = raw.associatedBarnas ?? raw.associated_barnas;
  const associatedBarnas = Array.isArray(assocRaw)
    ? assocRaw.map(normalizeAssociatedBarna).filter((x): x is AssociatedBarna => x != null)
    : [];
  return {
    id,
    name: toStr(raw.name),
    synonyms,
    associatedBarnas,
  };
}

function normalizeList(data: unknown): BarnaSamuha[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows.map(normalizeBarnaSamuha).filter((x): x is BarnaSamuha => x != null);
}

export async function listBarnaSamuha(): Promise<BarnaSamuha[]> {
  const res = await dictionaryApi.get<unknown>('/barna-samuha');
  return normalizeList(res.data);
}

export async function getBarnaSamuha(id: number): Promise<BarnaSamuha> {
  const res = await dictionaryApi.get<unknown>(`/barna-samuha/${id}`);
  const row = normalizeBarnaSamuha(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid barna samuha response');
  return row;
}

export async function createBarnaSamuha(body: CreateBarnaSamuhaBody): Promise<BarnaSamuha> {
  const res = await dictionaryApi.post<unknown>('/barna-samuha', body);
  const row = normalizeBarnaSamuha(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid barna samuha response');
  return row;
}

export async function updateBarnaSamuha(id: number, body: UpdateBarnaSamuhaBody): Promise<BarnaSamuha> {
  const res = await dictionaryApi.patch<unknown>(`/barna-samuha/${id}`, body);
  const row = normalizeBarnaSamuha(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid barna samuha response');
  return row;
}

export async function deleteBarnaSamuha(id: number): Promise<void> {
  await dictionaryApi.delete(`/barna-samuha/${id}`);
}

function synonymsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}

function associatedEqual(a: AssociatedBarna[], b: AssociatedBarna[]): boolean {
  if (a.length !== b.length) return false;
  const norm = (rows: AssociatedBarna[]) =>
    [...rows]
      .map((r) => ({
        barnaId: r.barnaId,
        includedBhedas: [...r.includedBhedas].sort((x, y) => x - y),
      }))
      .sort((x, y) => x.barnaId - y.barnaId);
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
}

export function buildBarnaSamuhaPatch(before: BarnaSamuha, after: BarnaSamuha): UpdateBarnaSamuhaBody {
  const patch: UpdateBarnaSamuhaBody = {};
  if (after.name !== before.name) patch.name = after.name;
  if (!synonymsEqual(after.synonyms, before.synonyms)) patch.synonyms = after.synonyms;
  if (!associatedEqual(after.associatedBarnas, before.associatedBarnas)) {
    patch.associated_barnas = associatedBarnasToBody(after.associatedBarnas);
  }
  return patch;
}
